import { useState } from "react";
import { Link } from "react-router-dom";
import { DataGrid, DataGridColumn } from "@/ui-controls/DataGrid";
import MoreActionsButton from "@/ui-controls/MoreActionsButton";
import {
  Persona,
  PersonaSize,
  Spinner,
  SpinnerSize,
  MessageBar,
  MessageBarType,
} from "@fluentui/react";
import {
  AppRegistration,
  deleteAppRegistration,
  generateNewAppRegistrationSecret,
  getAppRegistrationSecret,
} from "@/api/appRegistrations";
import { Text } from "@fluentui/react/lib/Text";

interface Props {
  CustomerId: number;
  AppRegList: AppRegistration[];
  onRemoveAppRegistration?: (appRegistration: AppRegistration) => any;
  onEditAppRegistration?: (appRegistration: AppRegistration) => any;
  onRefresh?: () => any;
}

const CustomerAppRegistrationsDetailList = (props: Props) => {
  const [isBusy, setIsBusy] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const handleError = (error: any) => {
    console.error(error);
    setErrorMessage(
      error?.message || "An unexpected error occurred. Please try again."
    );
  };

  const generateNewSecret = async (id: number) => {
    setErrorMessage(null);
    setSuccessMessage(null);
    setIsBusy(true);
    try {
      const result = await generateNewAppRegistrationSecret(id);
      setSuccessMessage("New secret generated successfully.");
      setTimeout(() => {
        props.onRefresh?.();
      }, 1000);
    } catch (err) {
      handleError(err);
    } finally {
      setIsBusy(false);
    }
  };

  const deleteAppReg = async (id: number) => {
    setErrorMessage(null);
    setSuccessMessage(null);
    setIsBusy(true);
    try {
      await deleteAppRegistration(id);
      setSuccessMessage("App registration deleted.");
      props.onRemoveAppRegistration?.(
        props.AppRegList.find((a) => a.Id === id)!
      );
    } catch (err) {
      handleError(err);
    } finally {
      setIsBusy(false);
    }
  };

  const copySecretToClipboard = async (id: number) => {
    setErrorMessage(null);
    setSuccessMessage(null);
    setIsBusy(true);
    try {
      const secret = await getAppRegistrationSecret(id);

      if (!secret) {
        setErrorMessage("Secret value is not available.");
        return;
      }

      try {
        await navigator.clipboard.writeText(secret);
        setSuccessMessage("Secret copied to clipboard.");
      } catch (copyError) {
        console.error(copyError);
        const textArea = document.createElement("textarea");
        textArea.value = secret;
        document.body.appendChild(textArea);
        textArea.select();
        try {
          document.execCommand("copy");
          setSuccessMessage("Secret copied to clipboard.");
        } catch (fallbackError) {
          console.error(fallbackError);
          setErrorMessage("Failed to copy secret to clipboard.");
        }
        document.body.removeChild(textArea);
      }
    } catch (err) {
      handleError(err);
    } finally {
      setIsBusy(false);
    }
  };

  const columns: DataGridColumn[] = [
    {
      key: "id",
      name: "Id",
      fieldName: "Id",
      minWidth: 15,
      maxWidth: 20,
      isRowHeader: true,
      isResizable: true,
      isPadded: true,
    },
    {
      key: "persona",
      name: "",
      fieldName: "",
      minWidth: 50,
      maxWidth: 50,
      isResizable: false,
      onRender: (item: AppRegistration) => (
        <div style={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Persona
            hidePersonaDetails
            text={item.ClientName}
            size={PersonaSize.size32}
          />
        </div>
      ),
    },
    {
      key: "clientName",
      name: "Client Name",
      fieldName: "ClientName",
      minWidth: 180,
      maxWidth: 250,
      isRowHeader: true,
      isResizable: true,
      isPadded: true,
    },
    {
      key: "appRegistrationName",
      name: "App Registration Name",
      fieldName: "AppRegistrationName",
      minWidth: 180,
      maxWidth: 250,
      isRowHeader: true,
      isResizable: true,
      isPadded: true,
    },
    {
      key: "contactEmail",
      name: "Contact Email",
      fieldName: "ContactEmail",
      minWidth: 200,
      maxWidth: 250,
      isRowHeader: true,
      isResizable: true,
      isPadded: true,
    },
    {
      key: "objectId",
      name: "Object Id",
      fieldName: "ObjectId",
      minWidth: 280,
      maxWidth: 300,
      isRowHeader: true,
      isResizable: true,
      isPadded: true,
    },
    {
      key: "secretExpirationDate",
      name: "Secret Expiration Date",
      fieldName: "SecretExpirationDate",
      minWidth: 130,
      maxWidth: 150,
      isRowHeader: true,
      isResizable: true,
      isPadded: true,
      onRender: (item: AppRegistration) => {
        if (!item.SecretExpirationDate) {
          return <Text styles={{ root: { fontStyle: "italic" } }}>N/A</Text>;
        }
        const exp = new Date(item.SecretExpirationDate);
        return <Text>{exp.toLocaleDateString()}</Text>;
      },
    },
    {
      key: "status",
      name: "Secret Status",
      fieldName: "SecretExpirationDate",
      minWidth: 150,
      maxWidth: 200,
      isRowHeader: true,
      isResizable: true,
      isPadded: true,
      onRender: (item: AppRegistration) => {
        const raw = item.SecretExpirationDate;
        if (!raw) {
          return <Text>N/A</Text>;
        }
        const exp = new Date(raw);
        const delta = exp.getTime() - Date.now();
        const oneWeekMs = 7 * 24 * 60 * 60 * 1000;
        let status: string, color: string;
        if (delta < 0) {
          status = "Expired";
          color = "#A4262C";
        } else if (delta < oneWeekMs) {
          status = "Expiring soon";
          color = "#D29200";
        } else {
          status = "Active";
          color = "#107C10";
        }
        return (
          <Text
            styles={{
              root: { color, fontWeight: delta < 0 ? "bold" : undefined },
            }}
          >
            {status}
          </Text>
        );
      },
    },
    {
      key: "SecretVaultUrl",
      name: "SecretVaultUrl",
      fieldName: "SecretVaultUrl",
      minWidth: 200,
      maxWidth: 300,
      isRowHeader: true,
      isResizable: true,
      isPadded: true,
      onRender: (item: AppRegistration) => (
        <Link
          to={item.SecretVaultUrl}
          target="_blank"
          style={{ textDecoration: "underline", color: "#0078D4" }}
        >
          {item.SecretVaultUrl}
        </Link>
      ),
    },
    {
      key: "Actions",
      name: "",
      minWidth: 50,
      maxWidth: 70,
      onRender: (item: AppRegistration) => (
        <MoreActionsButton
          menu={[
            {
              key: "edit",
              text: "Edit app registration",
              iconProps: { iconName: "Edit" },
              onClick: () => props.onEditAppRegistration?.(item),
            },
            {
              key: "secret",
              text: "Generate new secret",
              iconProps: { iconName: "Lock" },
              onClick: () => generateNewSecret(item.Id),
            },
            {
              key: "copySecret",
              text: "Copy secret value",
              iconProps: { iconName: "Copy" },
              onClick: () => copySecretToClipboard(item.Id),
            },
            {
              key: "remove",
              text: "Remove app registration",
              iconProps: { iconName: "Cancel" },
              onClick: () => deleteAppReg(item.Id),
            },
          ]}
        />
      ),
    },
  ];

  return (
    <>
      {isBusy && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 9999,
          }}
        >
          <div
            style={{
              backgroundColor: "white",
              padding: "24px",
              borderRadius: "8px",
              boxShadow: "0 4px 16px rgba(0, 0, 0, 0.3)",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: "16px",
            }}
          >
            <Spinner size={SpinnerSize.large} />
            <Text variant="medium" styles={{ root: { color: "#323130" } }}>
              Processing...
            </Text>
          </div>
        </div>
      )}

      <div
        style={{
          position: "relative" /* ensure overlay covers only this panel */,
        }}
      >
        <DataGrid items={[...props.AppRegList]} columns={columns} />
      </div>

      {successMessage && (
        <MessageBar
          messageBarType={MessageBarType.success}
          isMultiline={false}
          onDismiss={() => setSuccessMessage(null)}
          styles={{
            root: {
              position: "fixed",
              bottom: 0,
              left: 0,
              width: "100%",
              zIndex: 1000,
            },
          }}
        >
          {successMessage}
        </MessageBar>
      )}

      {errorMessage && (
        <MessageBar
          messageBarType={MessageBarType.error}
          isMultiline={false}
          onDismiss={() => setErrorMessage(null)}
          styles={{
            root: {
              position: "fixed",
              bottom: 0,
              left: 0,
              width: "100%",
              zIndex: 1000,
            },
          }}
        >
          {errorMessage}
        </MessageBar>
      )}
    </>
  );
};

export default CustomerAppRegistrationsDetailList;
